/**
 * Tear down service workers and caches, then hard-reload to pull the new build.
 *
 * Why the order and concurrency matter
 * ------------------------------------
 * The Workbox service worker answers navigations from its precache and does so
 * regardless of the URL's query string — so a cache-busting `?_cache_bust=`
 * param alone does NOT get us a fresh document while that worker is alive and
 * its precache is populated.
 *
 * What actually forces freshness is emptying the caches: with the precache gone,
 * the worker falls back to the network, so even if it still controls the reload
 * navigation it fetches a fresh index.html and its new hashed chunks. Clearing
 * caches is therefore the load-bearing step; unregistering the worker is
 * secondary hygiene so any later load is uncontrolled too.
 *
 * Both run concurrently under a single time-box. The previous version ran them
 * sequentially — unregister first, then clear caches — with no timeout at all.
 * Unregistering a busy worker can hang, and when it did the cache-clear never
 * ran and the navigation never fired: clicking "Update now" did nothing. Running
 * them concurrently means a slow or hung unregister can never starve the
 * cache-clear, while the worst-case wait stays bounded by the single timeout,
 * after which we navigate regardless.
 */

import { showUpdatingOverlay } from './update-overlay';

const TEARDOWN_TIMEOUT_MS = 2000;

function delay(ms: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

async function clearCaches(): Promise<void> {
  if (!('caches' in window)) {
    return;
  }
  const cacheNames = await caches.keys();
  await Promise.all(cacheNames.map(name => caches.delete(name)));
}

async function unregisterServiceWorkers(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map(registration => registration.unregister()));
}

/**
 * Empty the caches and unregister service workers, concurrently. `allSettled`
 * so a failure or hang in one never blocks the other — clearing the precache
 * (the step that defeats the service worker) must not wait behind unregister.
 */
async function teardownCachesAndWorkers(): Promise<void> {
  await Promise.allSettled([clearCaches(), unregisterServiceWorkers()]);
}

/**
 * Clear all caches and service workers, then force a fresh navigation.
 *
 * Resolves only if navigation somehow doesn't tear down the page; in practice
 * the `location.replace` ends this execution context.
 */
export async function forceReloadForUpdate(): Promise<void> {
  // Show the "Updating…" splash immediately so the user gets instant feedback
  // and the current view is masked before teardown and the blank navigation.
  // The new document re-shows the same splash and fades it out once it boots.
  showUpdatingOverlay();

  try {
    await Promise.race([teardownCachesAndWorkers(), delay(TEARDOWN_TIMEOUT_MS)]);
  } catch {
    // Teardown failed outright — navigate anyway below.
  } finally {
    // Cache-bust so intermediaries can't serve the stale document.
    const url = new URL(window.location.href);
    url.searchParams.set('_cache_bust', Date.now().toString());
    window.location.replace(url.toString());
  }
}
